import Dexie, { type Table } from 'dexie';
import type { EntityType } from '@/domain/schema/v1';
import type { Envelope } from '../crypto/envelope';

/**
 * IndexedDB 레이아웃 (계획서 §5). 평문은 불투명 식별자와 숫자 메타뿐이다.
 * clientCode·상태·날짜·내용은 모두 env(암호문) 안에 있다.
 *
 * Dexie version = 저장 레이아웃 버전. entity 스키마 버전(schemaVersion)과 별개다.
 */
export type StoredRecord = {
  entityType: EntityType;
  id: string;
  clientId: string; // 없으면 '' (Dexie 인덱스는 undefined를 색인하지 않음)
  sessionId: string;
  revision: number;
  schemaVersion: number;
  updatedAt: string;
  deleted: 0 | 1;
  env: Envelope;
};

export type BlobChunk = { blobId: string; chunk: number; total: number; env: Envelope };

export type OutboxItem = { seq?: number; entityType: EntityType; id: string; revision: number; queuedAt: string };

/** payload는 이미 암호화된 행들의 복사본 (평문 없음) */
export type SnapshotRow = {
  id: string;
  createdAt: string;
  reason: SnapshotReason;
  schemaVersion: number;
  payload: unknown;
};
export type SnapshotReason = 'daily' | 'pre-migration' | 'pre-restore' | 'pre-import' | 'manual';

export type MetaRow = { key: string; value: unknown };

/** 동기화로 알게 된 다른 기기 버전의 지문 (내용 없음). 조상 판정용. */
export type AncestryRow = { entityType: EntityType; id: string; hash: string; revision: number };

/** 다른 탭(이전 버전)이 저장소 업그레이드를 막고 있을 때 알림 */
const blockedListeners = new Set<() => void>();
export function onDbBlocked(cb: () => void): () => void {
  blockedListeners.add(cb);
  return () => blockedListeners.delete(cb);
}

export class CounselDb extends Dexie {
  meta!: Table<MetaRow, string>;
  entities!: Table<StoredRecord, [string, string]>;
  history!: Table<StoredRecord, [string, string, number]>;
  blobs!: Table<BlobChunk, [string, number]>;
  outbox!: Table<OutboxItem, number>;
  snapshots!: Table<SnapshotRow, string>;
  ancestry!: Table<AncestryRow, [string, string, string]>;

  constructor(name = 'counselai') {
    super(name);
    this.version(1).stores({
      meta: 'key',
      entities:
        '[entityType+id], entityType, [entityType+clientId], [entityType+sessionId], clientId, sessionId, updatedAt',
      history: '[entityType+id+revision], [entityType+id]',
      blobs: '[blobId+chunk], blobId',
      outbox: '++seq, [entityType+id]',
      snapshots: 'id, createdAt',
    });
    // 이 탭이 이전 버전이면: 새 버전이 업그레이드할 수 있게 연결을 닫고 새로고침을 안내한다
    this.on('versionchange', () => {
      this.close();
      if (typeof window !== 'undefined') window.dispatchEvent(new CustomEvent('counselai:db-closed'));
      return false;
    });
    this.on('blocked', () => blockedListeners.forEach((cb) => cb()));
    // v2 (M3): 동기화 조상 지문 저장소 추가. 기존 저장소는 그대로.
    this.version(2).stores({
      ancestry: '[entityType+id+hash], [entityType+id]',
    });
  }
}

export const META = {
  vault: 'vault',
  deviceId: 'deviceId',
  dataSchemaVersion: 'dataSchemaVersion',
  autoLockMinutes: 'autoLockMinutes',
  lastBackupAt: 'lastBackupAt',
  /** 마지막 Drive 백업 시각 (이 기기 기준, 자동 백업 판단용) */
  lastDriveBackupAt: 'lastDriveBackupAt',
  lastSnapshotAt: 'lastSnapshotAt',
  syncSeen: 'sync.seen',
  syncStatus: 'sync.status',
  driveConnected: 'sync.driveConnected',
  /** 이 기기 로그인 유지: 내보낼 수 없는(non-extractable) 데이터 키 + 만료. 동기화·백업·스냅샷에 포함되지 않는다 */
  remembered: 'device.remembered',
} as const;
